import type { MessageTemplate } from '@k-ucap/shared';
import type { MessageTemplateRepository } from './RepositoryInterfaces.js';

const templates: Map<string, MessageTemplate> = new Map();

export class InMemoryMessageTemplateRepository implements MessageTemplateRepository {
  async findAll(): Promise<MessageTemplate[]> {
    return Array.from(templates.values()).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async findById(id: string): Promise<MessageTemplate | undefined> {
    return templates.get(id);
  }

  async findByProjectId(projectId: string): Promise<MessageTemplate[]> {
    return Array.from(templates.values())
      .filter((t) => t.projectId === projectId)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async findByMessageType(projectId: string, messageType: number): Promise<MessageTemplate[]> {
    return Array.from(templates.values())
      .filter((t) => t.projectId === projectId && t.messageType === messageType)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async save(template: MessageTemplate): Promise<MessageTemplate> {
    templates.set(template.id, template);
    return template;
  }

  async delete(id: string): Promise<void> {
    templates.delete(id);
  }

  async duplicate(id: string, newName?: string): Promise<MessageTemplate | undefined> {
    const original = await this.findById(id);
    if (!original) return undefined;
    const duplicated: MessageTemplate = {
      ...original,
      id: `template-${Date.now()}`,
      name: newName ?? `${original.name} (Copy)`,
      parentId: original.id,
      revision: 1,
      updatedAt: new Date().toISOString(),
    };
    await this.save(duplicated);
    return duplicated;
  }
}

export const createInMemoryMessageTemplateRepository = (): InMemoryMessageTemplateRepository =>
  new InMemoryMessageTemplateRepository();
